"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Leaf, ShieldCheck, Clock, ArrowRight } from "lucide-react";
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
} = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const LeafParticles = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationFrameId: number;
    let particles: Array<{
      x: number;
      y: number;
      size: number;
      speedY: number;
      speedX: number;
      rotation: number;
      rotationSpeed: number;
      opacity: number;
    }> = [];
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", resize);
    resize();
    for (let i = 0; i < 25; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 8 + 4,
        speedY: Math.random() * 1 + 0.3,
        speedX: Math.random() * 0.6 - 0.3,
        rotation: Math.random() * 360,
        rotationSpeed: Math.random() * 1.5 - 0.75,
        opacity: Math.random() * 0.4 + 0.1
      });
    }
    const drawLeaf = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rotation: number, opacity: number) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation * Math.PI / 180);
      ctx.globalAlpha = opacity;
      ctx.fillStyle = "hsl(145, 15%, 45%)";
      ctx.beginPath();
      ctx.ellipse(0, 0, size, size / 1.5, 0, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();
    };
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.y += p.speedY;
        p.x += p.speedX;
        p.rotation += p.rotationSpeed;
        if (p.y > canvas.height + 20) {
          p.y = -20;
          p.x = Math.random() * canvas.width;
        }
        drawLeaf(ctx, p.x, p.y, p.size, p.rotation, p.opacity);
      });
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" aria-hidden="true" />;
};
export default function TakingCareOfYourGiants() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="228894" id="همراه-شما-در-مسیر-آرامش" className="relative w-full min-h-[90vh] flex items-center bg-background text-foreground overflow-hidden pt-24 pb-16 lg:pt-32 lg:pb-24">
      {}
      <LeafParticles />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl" dir="rtl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={contentRef} className={`flex flex-col items-start space-y-8 transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-accent text-accent-foreground px-4 py-1.5 text-sm font-medium rounded-md shadow-sm flex items-center gap-2">
                <Clock className="w-4 h-4" />
                ۱۰ سال تجربه بالینی
              </Badge>
              <Badge className="bg-accent text-accent-foreground px-4 py-1.5 text-sm font-medium rounded-md shadow-sm flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                متخصص روانشناسی سلامت
              </Badge>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-[5rem] font-default font-bold leading-[1.2] tracking-tight text-foreground">
              همراه شما در{" "}
              <span className="block mt-2">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-ring to-secondary inline-block pb-2 drop-shadow-sm">
                  مسیر آرامش
                </span>
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground max-w-lg leading-relaxed font-default">
              دکتر مهرنوش اسماعیلی، متخصص روانشناسی با رویکردی علمی و همدلانه، شما را در مواجهه با چالش‌های زندگی و بازیابی سلامت روان یاری می‌کند.
            </p>

            <div className="pt-4 flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-ring text-white hover:bg-ring/90 text-lg px-8 py-7 rounded-md shadow-md hover:shadow-lg transition-all duration-300 group">
                <Link to="/BookAppointmentPage">
                  رزرو نوبت مشاوره
                  <ArrowRight className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform rotate-180" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`relative w-full h-full flex justify-center lg:justify-end items-center transition-all duration-1000 delay-300 ease-out ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            {}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-secondary/20 rounded-md blur-3xl -z-10" />
            
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684933/img/dr_esmaeili_young_calm_portrait.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684933/img/dr_esmaeili_young_calm_portrait.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684933/img/dr_esmaeili_young_calm_portrait.webp-wvc-sizes" alt="دکتر مهرنوش اسماعیلی - متخصص روانشناسی" className="w-full max-w-md lg:max-w-none h-auto object-contain drop-shadow-xl lg:-mt-16 xl:-mt-24 relative z-10 rounded-md" loading="eager" />
            
            {}
            <div className="absolute top-1/4 left-1/4 animate-bounce hidden lg:block z-20 text-secondary/60">
              <Leaf className="w-8 h-8 -rotate-45" />
            </div>
          </div>

        </div>
      </div>
    </section>;
}